import type { HealthResponse } from '@siteshowroom/shared'

/**
 * Kleine, getypeerde fetch-wrapper voor de platform-API.
 *
 * Uitgangspunt: een aanroep werpt nooit een exception naar de UI. Alle drie de
 * manieren waarop een aanvraag stuk kan gaan — geen verbinding, een HTTP-fout,
 * of een antwoord dat geen bruikbare JSON is — komen terug als een herkenbaar
 * ApiFailure met een `kind`. De component kan daarop schakelen zonder try/catch.
 */

export type ApiFailureKind =
  /** Geen antwoord: server uit, DNS-fout, proxy niet bereikbaar. */
  | 'network'
  /** De aanroeper heeft de aanvraag zelf afgebroken via AbortSignal. */
  | 'aborted'
  /** Wel een antwoord, maar geen 2xx-status. */
  | 'http'
  /** Wel een 2xx-antwoord, maar de body is geen geldige of geen verwachte JSON. */
  | 'invalid-response'

export interface ApiFailure {
  readonly kind: ApiFailureKind
  /** Technische omschrijving voor ontwikkelaars; niet bedoeld als UI-tekst. */
  readonly message: string
  /** Alleen aanwezig bij kind === 'http'. */
  readonly status?: number
}

export type ApiResult<T> =
  | { readonly ok: true; readonly data: T; readonly roundTripMs: number }
  | { readonly ok: false; readonly error: ApiFailure }

export interface RequestOptions {
  readonly signal?: AbortSignal
}

/**
 * Basis-URL van de API. Leeg (de standaard tijdens `pnpm dev`) betekent: praat
 * met /api op de eigen origin en laat de Vite-proxy het werk doen — geen CORS.
 */
function resolveBaseUrl(): string {
  const raw: unknown = import.meta.env.VITE_API_BASE_URL
  const configured = typeof raw === 'string' ? raw.trim() : ''
  if (configured === '') {
    return '/api'
  }
  return configured.replace(/\/+$/, '')
}

const API_BASE_URL = resolveBaseUrl()

export function apiUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE_URL}${normalized}`
}

function isAbortError(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError'
}

function describeCause(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.message
  }
  return String(cause)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Voert de aanvraag uit en levert de ruwe JSON als `unknown`; valideren gebeurt per endpoint. */
async function requestJson(path: string, options: RequestOptions): Promise<ApiResult<unknown>> {
  const startedAt = performance.now()

  let response: Response
  try {
    response = await fetch(apiUrl(path), {
      method: 'GET',
      headers: { accept: 'application/json' },
      signal: options.signal ?? null,
    })
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  if (!response.ok) {
    const statusText = response.statusText === '' ? '' : ` ${response.statusText}`
    return {
      ok: false,
      error: {
        kind: 'http',
        status: response.status,
        message: `${path}: HTTP ${String(response.status)}${statusText}`,
      },
    }
  }

  let payload: unknown
  try {
    payload = await response.json()
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return {
      ok: false,
      error: {
        kind: 'invalid-response',
        message: `${path}: invalid JSON (${describeCause(cause)})`,
      },
    }
  }

  return { ok: true, data: payload, roundTripMs: performance.now() - startedAt }
}

/**
 * Controleert of de body overeenkomt met `HealthResponse` uit
 * @siteshowroom/shared — het gedeelde contract tussen API en dashboard.
 * Levert een omschrijving van het eerste probleem, of null als alles klopt.
 * Zo wordt een afwijkend API-antwoord zichtbaar in plaats van als `undefined`
 * op het scherm te belanden.
 */
function describeCheckProblem(value: unknown, name: string): string | null {
  if (!isRecord(value)) {
    return `missing "checks.${name}"`
  }
  if (typeof value['status'] !== 'string') {
    return `missing "checks.${name}.status"`
  }
  const latency = value['latencyMs']
  if (latency !== null && typeof latency !== 'number') {
    return `"checks.${name}.latencyMs" must be a number or null`
  }
  return null
}

function describeHealthProblem(value: unknown): string | null {
  if (!isRecord(value)) {
    return 'body is not an object'
  }
  if (typeof value['status'] !== 'string') {
    return 'missing "status"'
  }
  const checks = value['checks']
  if (!isRecord(checks)) {
    return 'missing "checks" object'
  }

  for (const name of ['database', 'storage', 'mail']) {
    const problem = describeCheckProblem(checks[name], name)
    if (problem !== null) {
      return problem
    }
  }

  const database = checks['database']
  if (isRecord(database)) {
    const applied = database['appliedMigrations']
    if (applied !== null && typeof applied !== 'number') {
      return '"checks.database.appliedMigrations" must be a number or null'
    }
  }

  const storage = checks['storage']
  if (isRecord(storage) && typeof storage['driver'] !== 'string') {
    return 'missing "checks.storage.driver"'
  }

  const mail = checks['mail']
  if (isRecord(mail) && typeof mail['driver'] !== 'string') {
    return 'missing "checks.mail.driver"'
  }

  return null
}

/**
 * GET /health. Valideert de body voordat hij als HealthResponse wordt doorgegeven.
 */
export async function fetchHealth(
  options: RequestOptions = {},
): Promise<ApiResult<HealthResponse>> {
  const result = await requestJson('/health', options)
  if (!result.ok) {
    return result
  }

  const problem = describeHealthProblem(result.data)
  if (problem !== null) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `/health: ${problem}` },
    }
  }

  // De vorm is hierboven runtime-gecontroleerd; de cast maakt dat expliciet.
  return { ok: true, data: result.data as HealthResponse, roundTripMs: result.roundTripMs }
}
